import { useState, useMemo } from "react";
import { useVelvet } from "@/context/VelvetContext";
import { useAuth, canSeeCosts } from "@/context/AuthContext";
import { fmtUSD, fmtDate } from "@/lib/calc";
import { apiFetch } from "@/lib/supabase";
import StatusBadge from "@/components/StatusBadge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Truck, Store, Calendar, User } from "lucide-react";
import { toast } from "sonner";

const ALLOWED = {
  Received: ['Confirmed','Cancelled'],
  Confirmed: ['Queued','Cancelled'],
  Queued: ['In Production','Cancelled'],
  'In Production': ['Blocked','QC','Cancelled'],
  Blocked: ['In Production','Cancelled'],
  QC: ['Ready','In Production'],
  Ready: ['Out for Delivery','Cancelled'],
  'Out for Delivery': ['Delivered','Failed Delivery'],
  'Failed Delivery': ['Out for Delivery','Cancelled'],
  Delivered: [],
  Cancelled: [],
};

export default function Orders() {
  const { role } = useAuth();
  const { orders, deliveries, customers, designers, drivers, orderStatuses, reloadOrders, reloadDeliveries } = useVelvet();
  const [detailId, setDetailId] = useState(null);
  const [assignDriverFor, setAssignDriverFor] = useState(null); // delivery id
  const detail = detailId ? orders.find((o) => o.id === detailId) : null;
  const showCosts = canSeeCosts(role);

  const grouped = useMemo(() => {
    const g = Object.fromEntries(orderStatuses.map((s) => [s, []]));
    orders.forEach((o) => { (g[o.status] ||= []).push(o); });
    return g;
  }, [orders, orderStatuses]);

  const transition = async (orderId, newStatus, reason) => {
    try {
      await apiFetch(`/orders/${orderId}/transition`, {
        method: 'POST',
        body: JSON.stringify({ new_status: newStatus, reason }),
      });
      toast.success(`Moved to ${newStatus}`);
      reloadOrders();
    } catch (e) {
      toast.error(e.message);
    }
  };

  return (
    <div className="px-4 sm:px-6 lg:px-10 py-8 lg:py-12" data-testid="orders-page">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8 max-w-[1400px]">
        <div>
          <div className="velvet-eyebrow mb-3">Production floor · live</div>
          <h1 className="velvet-page-title">Orders &amp; Production</h1>
          <p className="text-sm text-muted-foreground mt-3 max-w-xl">
            Every arrangement, from received to delivered — assign designers &amp; drivers, and move the day forward.
          </p>
        </div>
      </div>

      <div className="overflow-x-auto -mx-4 sm:-mx-6 lg:-mx-10 px-4 sm:px-6 lg:px-10 pb-6" data-testid="orders-kanban">
        <div className="flex gap-4 min-w-max">
          {orderStatuses.map((status) => (
            <div key={status} className="w-80 shrink-0" data-testid={`kanban-column-${status.toLowerCase().replace(/\s+/g,'-')}`}>
              <div className="flex items-baseline justify-between px-2 mb-3">
                <span className="velvet-eyebrow">{status} <span className="text-muted-foreground">· {grouped[status].length}</span></span>
              </div>
              <div className="bg-[hsl(var(--muted))]/40 border border-border p-3 min-h-[400px] flex flex-col gap-3">
                {grouped[status].length === 0 && <div className="text-xs text-muted-foreground text-center py-8">No orders</div>}
                {grouped[status].map((o) => {
                  const c = customers.find((x) => x.id === o.customer_id);
                  const d = designers.find((x) => x.id === o.designer_id);
                  const total = (o.order_lines || []).reduce((s, li) => s + li.unit_price * li.qty, 0);
                  return (
                    <button key={o.id} onClick={() => setDetailId(o.id)}
                      className="text-left velvet-card p-4 hover:-translate-y-[2px] transition-transform"
                      data-testid={`order-card-${o.number}`}>
                      <div className="flex items-baseline justify-between gap-2">
                        <div className="font-mono text-[11px] text-muted-foreground">{o.number}</div>
                        {showCosts && <div className="velvet-money text-xs">{fmtUSD(total)}</div>}
                      </div>
                      <div className="font-serif text-lg leading-tight mt-1">
                        {c?.name || '—'}
                        {o.is_demo && <span className="ml-2 align-middle text-[9px] px-1 py-0.5 border border-border text-muted-foreground uppercase tracking-wider">DEMO</span>}
                      </div>
                      <div className="text-xs text-muted-foreground mt-0.5 line-clamp-1">{o.occasion || '—'}</div>
                      <div className="mt-3 flex items-center gap-3 text-xs">
                        <span className="inline-flex items-center gap-1 text-muted-foreground">
                          <Calendar className="h-3 w-3" />
                          {o.delivery_date ? new Date(o.delivery_date).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', timeZone: 'America/Chicago' }) : '—'}
                        </span>
                      </div>
                      <div className="mt-3 flex items-center justify-between">
                        <span className={`inline-flex items-center gap-1 text-[11px] px-2 py-0.5 border ${o.fulfillment === 'Delivery' ? 'border-[hsl(var(--muted-green))]/50 bg-[hsl(var(--muted-green))]/10 text-[hsl(var(--muted-green-dark))]' : 'border-[hsl(var(--soft-blush-dark))] bg-[hsl(var(--soft-blush))]'}`}>
                          {o.fulfillment === 'Delivery' ? <Truck className="h-3 w-3" /> : <Store className="h-3 w-3" />}
                          {o.fulfillment}
                        </span>
                        {d ? (
                          <span className="text-[11px] text-muted-foreground truncate max-w-[120px]">{d.full_name || d.email}</span>
                        ) : (
                          <span className="text-[11px] text-muted-foreground inline-flex items-center gap-1"><User className="h-3 w-3" /> Unassigned</span>
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>

      <OrderDetailDialog
        order={detail}
        onClose={() => setDetailId(null)}
        onTransition={transition}
        onAssignDesigner={async (did) => {
          try {
            await apiFetch(`/orders/${detail.id}/assign-designer`, {
              method: 'POST',
              body: JSON.stringify({ designer_id: did }),
            });
            toast.success('Designer assigned');
            reloadOrders();
          } catch (e) { toast.error(e.message); }
        }}
        onAssignDriver={() => {
          const del = deliveries.find((x) => x.order_id === detail.id);
          if (!del) return toast.error('No delivery record yet. Move order to Ready first.');
          setAssignDriverFor(del.id);
        }}
        deliveries={deliveries}
      />

      <AssignDriverDialog
        deliveryId={assignDriverFor}
        drivers={drivers}
        onClose={() => setAssignDriverFor(null)}
        onSubmit={async ({ driver_id, delivery_instructions }) => {
          try {
            await apiFetch(`/deliveries/${assignDriverFor}/assign-driver`, {
              method: 'POST',
              body: JSON.stringify({ driver_id, delivery_instructions }),
            });
            toast.success('Driver assigned');
            setAssignDriverFor(null);
            reloadDeliveries();
          } catch (e) { toast.error(e.message); }
        }}
      />
    </div>
  );
}

function OrderDetailDialog({ order, onClose, onTransition, onAssignDesigner, onAssignDriver, deliveries }) {
  const { role } = useAuth();
  const { customers, designers } = useVelvet();
  const [blockReason, setBlockReason] = useState('');
  if (!order) return null;
  const c = customers.find((x) => x.id === order.customer_id);
  const delivery = deliveries.find((d) => d.order_id === order.id);
  const showCosts = canSeeCosts(role);
  const total = (order.order_lines || []).reduce((s, li) => s + li.unit_price * li.qty, 0);
  const cost = (order.order_lines || []).reduce((s, li) => s + li.unit_cost_snapshot * li.qty, 0);
  const nextOptions = ALLOWED[order.status] || [];

  return (
    <Dialog open={!!order} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl rounded-sm max-h-[92vh] overflow-y-auto" data-testid="order-detail-dialog">
        <DialogHeader>
          <div className="velvet-eyebrow flex items-center gap-2">
            {order.number}{order.is_demo && <span className="text-[10px] px-1.5 py-0.5 border border-border text-muted-foreground uppercase tracking-wider">DEMO</span>}
          </div>
          <DialogTitle className="font-serif text-3xl tracking-tight">
            {c?.name} · {order.occasion || '—'}
          </DialogTitle>
        </DialogHeader>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2">
          <div>
            <Label>Current status</Label>
            <div className="mt-1"><StatusBadge status={order.status} /></div>
          </div>
          <div>
            <Label>Fulfillment</Label>
            <div className="mt-1 text-sm">{order.fulfillment}</div>
          </div>
          <div>
            <Label>Delivery / pickup</Label>
            <div className="mt-1 text-sm font-mono">{order.delivery_date ? new Date(order.delivery_date).toLocaleString('en-US', { timeZone: 'America/Chicago' }) : '—'}</div>
          </div>
          {['admin','manager'].includes(role) && (
            <div>
              <Label>Assigned designer</Label>
              <Select value={order.designer_id || 'none'} onValueChange={(v) => onAssignDesigner(v === 'none' ? null : v)}>
                <SelectTrigger className="rounded-sm" data-testid="order-designer-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Unassigned</SelectItem>
                  {designers.map((d) => (<SelectItem key={d.id} value={d.id}>{d.full_name || d.email}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>

        {order.address && (
          <div className="mt-4">
            <Label>{order.fulfillment === 'Delivery' ? 'Delivery address' : 'Pickup note'}</Label>
            <div className="text-sm mt-1">{order.address}</div>
          </div>
        )}
        {order.production_notes && (
          <div className="mt-4">
            <Label>Production instructions</Label>
            <div className="text-sm mt-1 whitespace-pre-wrap">{order.production_notes}</div>
          </div>
        )}
        {order.card_message && (
          <div className="mt-4">
            <Label>Card message</Label>
            <div className="text-sm mt-1 whitespace-pre-wrap italic">"{order.card_message}"</div>
          </div>
        )}

        <div className="mt-6">
          <div className="velvet-eyebrow mb-3">Line items</div>
          <div className="border border-border">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border text-left bg-background/60">
                  <th className="px-3 py-2 velvet-eyebrow">Item</th>
                  <th className="px-3 py-2 velvet-eyebrow text-right w-16">Qty</th>
                  {showCosts && <th className="px-3 py-2 velvet-eyebrow text-right w-28">Price</th>}
                  {showCosts && <th className="px-3 py-2 velvet-eyebrow text-right w-28">Cost</th>}
                </tr>
              </thead>
              <tbody>
                {(order.order_lines || []).map((li) => (
                  <tr key={li.id} className="border-b border-border last:border-b-0">
                    <td className="px-3 py-2">{li.item_name}</td>
                    <td className="px-3 py-2 text-right velvet-money">{li.qty}</td>
                    {showCosts && <td className="px-3 py-2 text-right velvet-money">{fmtUSD(li.unit_price)}</td>}
                    {showCosts && <td className="px-3 py-2 text-right velvet-money text-muted-foreground">{fmtUSD(li.unit_cost_snapshot)}</td>}
                  </tr>
                ))}
              </tbody>
              {showCosts && (
                <tfoot>
                  <tr className="border-t-2 border-foreground/15">
                    <td className="px-3 py-2 velvet-eyebrow" colSpan={2}>Totals</td>
                    <td className="px-3 py-2 text-right velvet-money font-semibold">{fmtUSD(total)}</td>
                    <td className="px-3 py-2 text-right velvet-money text-muted-foreground">{fmtUSD(cost)}</td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </div>

        {/* Transition controls */}
        <div className="mt-6 border-t border-border pt-6">
          <div className="velvet-eyebrow mb-3">Move status</div>
          <div className="flex flex-wrap gap-2 items-center">
            {nextOptions.length === 0 && <span className="text-sm text-muted-foreground">No further transitions from this status.</span>}
            {nextOptions.map((next) => {
              const needsReason = next === 'Blocked';
              return (
                <div key={next} className="flex items-center gap-2">
                  {needsReason && (
                    <Input placeholder="Block reason" value={blockReason} onChange={(e) => setBlockReason(e.target.value)} className="rounded-sm h-8 w-40" data-testid="block-reason-input" />
                  )}
                  <Button
                    size="sm"
                    className="rounded-sm bg-foreground text-background hover:bg-foreground/90"
                    disabled={needsReason && !blockReason.trim()}
                    onClick={async () => {
                      await onTransition(order.id, next, needsReason ? blockReason.trim() : null);
                      setBlockReason('');
                    }}
                    data-testid={`transition-${next.toLowerCase().replace(/\s+/g,'-')}`}
                  >
                    → {next}
                  </Button>
                </div>
              );
            })}
            {['admin','manager'].includes(role) && order.status === 'Ready' && (
              <Button size="sm" variant="outline" className="rounded-sm" onClick={onAssignDriver} data-testid="assign-driver-btn">
                Assign driver{delivery?.driver_id ? ' (reassign)' : ''}
              </Button>
            )}
          </div>
        </div>

        <DialogFooter className="mt-6">
          <Button variant="outline" className="rounded-sm" onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AssignDriverDialog({ deliveryId, drivers, onClose, onSubmit }) {
  const [driver, setDriver] = useState('');
  const [instr, setInstr] = useState('');
  if (!deliveryId) return null;
  return (
    <Dialog open={!!deliveryId} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg rounded-sm" data-testid="assign-driver-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl">Assign driver</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 pt-2">
          <div>
            <Label>Driver</Label>
            <Select value={driver} onValueChange={setDriver}>
              <SelectTrigger className="rounded-sm" data-testid="driver-select"><SelectValue placeholder="Select driver" /></SelectTrigger>
              <SelectContent>
                {drivers.map((d) => (<SelectItem key={d.id} value={d.id}>{d.full_name || d.email}</SelectItem>))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Delivery instructions (optional)</Label>
            <Textarea value={instr} onChange={(e) => setInstr(e.target.value)} className="rounded-sm" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" className="rounded-sm" onClick={onClose}>Cancel</Button>
          <Button disabled={!driver} onClick={() => onSubmit({ driver_id: driver, delivery_instructions: instr || null })} className="rounded-sm bg-[hsl(var(--muted-green-dark))] hover:bg-[hsl(var(--muted-green-dark))]/90 text-white" data-testid="confirm-assign-driver">
            Assign
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
